import React from 'react';
import { Button, Table } from 'reactstrap';
import { FaChevronDown, FaChevronRight, FaTrash } from 'react-icons/fa';
import { DEFAULT_CONTAINER_TYPE_ICON } from '@/utils/imageUrl';
import i18n from '@/utils/i18n';
import { Link } from 'react-router-dom';
import { GetScanTunnelSessionQuery } from '../../../../graphql/queries/getScanTunnelSession.generated';

type ContainersTableProps = {
  session: GetScanTunnelSessionQuery['getScanTunnelSession'];
  removeItemsFromSession: (
    containerIds: string[],
    itemIds: string[],
  ) => Promise<void>;
  removingScannedItemsFromSession: boolean;
};

type ContainerType = {
  type_id: number;
  type_name: string;
  image_url?: string | null;
};

type ContainerTypeAmount = ContainerType & {
  amount: number;
};

export const ContainersTable: React.FC<ContainersTableProps> = ({
  session,
  removeItemsFromSession,
  removingScannedItemsFromSession,
}) => {
  const [containerTypeExpanded, setContainerTypeExpanded] = React.useState<
    number[]
  >([]);

  function groupContainersByType(containers: ContainerType[]) {
    const res: ContainerTypeAmount[] = [];
    for (const container of containers) {
      const existing = res.find((c) => c.type_id === container.type_id);
      if (existing) {
        existing.amount++;
      } else {
        res.push({
          type_id: container.type_id,
          type_name: container.type_name,
          image_url: container.image_url,
          amount: 1,
        });
      }
    }
    return res;
  }

  function toggleContainerTypeExpanded(typeId: number) {
    if (containerTypeExpanded.includes(typeId)) {
      setContainerTypeExpanded(
        containerTypeExpanded.filter((id) => id !== typeId),
      );
    } else {
      setContainerTypeExpanded([...containerTypeExpanded, typeId]);
    }
  }

  return (
    <Table className="striped">
      <thead>
        <tr>
          <th>{i18n.t('Common:type')}</th>
          <th>{i18n.t('Common:amount')}</th>
          <th></th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {session.containers.length === 0 && (
          <tr>
            <td colSpan={4}>{i18n.t('ScanTunnelSession:noContainers')}</td>
          </tr>
        )}
        {groupContainersByType(session.containers).map((type) => [
          <tr key={type.type_id}>
            <td>
              <img
                style={{ width: '50px', height: '50px' }}
                alt={`container type ${type.type_id} ${type.type_name}`}
                src={type.image_url || DEFAULT_CONTAINER_TYPE_ICON}
              ></img>
              <span className="pl-2">{type.type_name}</span>
            </td>
            <td>{type.amount}</td>
            <td>
              <Button onClick={() => toggleContainerTypeExpanded(type.type_id)}>
                {containerTypeExpanded.includes(type.type_id) ? (
                  <FaChevronRight />
                ) : (
                  <FaChevronDown />
                )}
              </Button>
            </td>
            <td>
              <Button
                className="btn-danger"
                onClick={() => {
                  removeItemsFromSession(
                    session.containers
                      .filter((c) => c.type_id === type.type_id)
                      .map((c) => c.id),
                    [],
                  );
                }}
                disabled={removingScannedItemsFromSession}
              >
                {removingScannedItemsFromSession ? '...' : <FaTrash />}
              </Button>
            </td>
          </tr>,
          containerTypeExpanded.includes(type.type_id) && (
            <tr key={type.type_id + '_details'}>
              <td colSpan={4}>
                <Table>
                  <thead>
                    <tr>
                      <th>{i18n.t('ScanTunnelSession:name')}</th>
                      <th>{i18n.t('ScanTunnelSession:ownerSince')}</th>
                      <th>{i18n.t('ScanTunnelSession:status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {session.containers
                      .filter((c) => c.type_id === type.type_id)
                      .map((c) => (
                        <tr key={c.id}>
                          <td>
                            <Link to={'/container/' + c.id}>{c.name}</Link>
                          </td>
                          <td>{c.owner_since}</td>
                          <td>{c.status}</td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </td>
            </tr>
          ),
        ])}
        {session.containers.length !== 0 && (
          <tr>
            <td>
              <b>Summe</b>
            </td>
            <td>
              <b>{session.containers.length}</b>
            </td>
            <td></td>
            <td></td>
          </tr>
        )}
      </tbody>
    </Table>
  );
};
